import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { flatten, uniq } from 'ramda';

import { bookingShape, historyShape } from 'constants/propTypes';
import moment from 'moment';

import { FormattedMessage } from 'react-intl';
import CanPerform from 'components/shared/CanPerform';
import { PRINT_ATB } from 'constants/afterSalesShortcuts';
import { ADD_ADDITIONAL_DETAILS, PRINT_CUSTOMER_PRESENT_TICKET, PRINT_TICKETS_ATB } from 'store/mutations';

import { MOBILE_TERMINAL_ID_STORAGE_KEY, MOBILE_TERMINAL_SERIAL_NO_STORAGE_KEY } from 'constants/user';
import Modal from 'components/ui/Modal/Modal';
import { ButtonWrapper, CtaButton, ModalContents } from '../AfterSalesCtas.styles';
import {
  runAfterSaleValidator,
  bookingIsConfirmed,
  allSelectedProductsConfirmedCheckedInOrPRRRefunded,
  containsSNCBItemRefs
} from '../AfterSalesCtas.utils';
import { isCanPrintOnMobile } from 'utils/isCanPrintOnMobile';
import { getBookingTravelDocumentsURL } from '../../../../../utils/urls';
import { ConfirmButton } from '../../../../../components/ui/Button';

const getAddOnLegItemRefs = (selectedItemRefs, booking) =>
  selectedItemRefs
    .map((itemRef) => {
      const bookingItem = booking.allBookingItems.find((item) => item.itemRef === itemRef);

      if (bookingItem && bookingItem.associatedItemRefs.length > 0) {
        return bookingItem.associatedItemRefs;
      }

      return null;
    })
    .filter(Boolean);

const PrintCta = ({
  label,
  disabled,
  booking,
  booking: { reference },
  selectedItemRefs,
  user,
  isPassportRequired,
  history,
  isThroughFare
}) => {
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const [isPassportOpen, setIsPassportOpen] = useState(false);
  const [printAtb, { loading }] = useMutation(PRINT_TICKETS_ATB);
  const [printCustomerPresentTicket, { loadingTerminal }] = useMutation(PRINT_CUSTOMER_PRESENT_TICKET);

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);

  const [isMobileIndirect, setIsMobileIndirect] = useState(false);

  const isSNCBLegSelected =
    containsSNCBItemRefs(selectedItemRefs, booking.outbound, false) || containsSNCBItemRefs(selectedItemRefs, booking.inbound, false);

  const isDisabled = Boolean(
    disabled ||
      (isSNCBLegSelected && !isThroughFare) ||
      !runAfterSaleValidator(booking, selectedItemRefs)([bookingIsConfirmed, allSelectedProductsConfirmedCheckedInOrPRRRefunded])
  );

  const terminalId = localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY);
  const terminalSerialNo = localStorage.getItem(MOBILE_TERMINAL_SERIAL_NO_STORAGE_KEY);

  const addOnLegsItemRefs = flatten(getAddOnLegItemRefs(selectedItemRefs, booking));

  const toggleModal = () => {
    setIsMobileIndirect(!isMobileIndirect);
  };

  const togglePassportModal = () => {
    setIsPassportOpen(!isPassportOpen);
  };

  const onCaptureTravelDocument = () => {
    history.push(getBookingTravelDocumentsURL(booking.reference, membershipNumber));
  };

  return (
    <>
      <Modal isOpen={isMobileIndirect} title="Error" onClose={toggleModal}>
        <FormattedMessage id="mobilePrint.noIndirect" />
      </Modal>

      <Modal isOpen={isPassportOpen} title="Error" onClose={togglePassportModal}>
        <ModalContents>
          <FormattedMessage id="issueTicket.passportRequired1" />
          <br />
          <FormattedMessage id="issueTicket.passportRequired2" />
        </ModalContents>
        <ButtonWrapper>
          <ConfirmButton variant="primary" onClick={onCaptureTravelDocument}>
            <FormattedMessage id="advancedPaxInfo.title" />
          </ConfirmButton>
        </ButtonWrapper>
      </Modal>

      <CanPerform afterSalesShortcut={PRINT_ATB}>
        {(isEligible) =>
          isEligible && (
            <CtaButton
              data-cy="print-button"
              type="button"
              label={label}
              loading={!!loading || !!loadingTerminal || !!addAdditionalDetailsLoading}
              disabled={isDisabled}
              onClick={() => {
                if (isPassportRequired) {
                  setIsPassportOpen(true);
                  return;
                }
                const isCanPrint = isCanPrintOnMobile(booking, selectedItemRefs);

                if (!isCanPrint) {
                  setIsMobileIndirect(true);
                }

                if (terminalId && isCanPrint) {
                  printCustomerPresentTicket({
                    variables: {
                      terminalId,
                      cashRegisterId: terminalSerialNo,
                      bookingReference: reference,
                      itemRefs: uniq([...selectedItemRefs, ...addOnLegsItemRefs])
                    }
                  });

                  return addAdditionalDetails({
                    variables: {
                      bookingReference: reference,
                      additionalDetails: [
                        {
                          key: `tablet-print-${moment().format('HH:mm:ss.DDMMYY')}`,
                          value: `${user.paymentLocation && user.paymentLocation.name}-${
                            selectedItemRefs.length + addOnLegsItemRefs.length
                          }`
                        }
                      ]
                    }
                  });
                }

                if (!terminalId) {
                  return printAtb({
                    variables: {
                      bookingReference: reference,
                      itemRefs: uniq([...selectedItemRefs, ...addOnLegsItemRefs])
                    }
                  });
                }

                return false;
              }}
            />
          )
        }
      </CanPerform>
    </>
  );
};

PrintCta.propTypes = {
  label: PropTypes.node,
  disabled: PropTypes.bool,
  booking: bookingShape.isRequired,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  user: PropTypes.shape({
    isStation: PropTypes.bool,
    paymentLocation: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  isPassportRequired: PropTypes.bool,
  history: historyShape,
  isThroughFare: PropTypes.bool
};

export default PrintCta;
